import { Button } from "@/components/button/Button"
import { Sidepanel } from "@/components/sidepanel/Sidepanel"
import { Form } from "@/components/ui/form"
import { DropdownField } from "@/components/ui/form/DropdownField"
import { InputField } from "@/components/ui/form/InputField"
import { tasksApi } from "@/fetch/api/tasks"
import { useWatcher } from "@/lib/state/useWatcher"
import { tenantState } from "@/store/tenant"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "react-toastify"
import { useEffectOnce } from "react-use"
import { Operation, Project } from "src/types"
import styled from "styled-components"

/**
	OpGroupID           int      `json:"opGroupId"`
	ProjectID           int      `json:"projectId"`
	WorkerCountExpected int      `json:"workerCountExpected"`
	TimePredicted       *int     `json:"timePredicted"`
	UnitsPredicted      *float32 `json:"unitsPredicted"`
	SalaryPredicted     *int     `json:"salaryPredicted"`
	Normative           *float64 `json:"normative"`

*/
const ButtonRow = styled.div`
  display: flex;
  margin: 10px;
  justify-content: flex-end;
  gap: 8px;
`
const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  line-height: 40px;
  letter-spacing: -0.02em;
  text-align: left;
  margin-bottom: 20px;
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
`

type FormInputs = {
  opGroupId: number[]
  projectId: number
  workerCountExpected: number
  timePredicted?: number
  unitsPredicted?: number
  salaryPredicted?: number
  normative?: number
  hours?: number
  minutes?: number
}

function formatGroups(
  groups: { id: number; name: string; parentId?: number }[],
): { id: number; name: string }[] {
  const items: { id: number; name: string }[] = []
  for (const g of groups) {
    if (!g.parentId) {
      // skip parents
      continue
    }
    const parent = groups.find(({ id }) => id === g.parentId)
    if (!parent) {
      continue
    }
    items.push({
      id: g.id,
      name: `${parent.name} - ${g.name}`,
    })
  }
  return items
}

export const AddNewOperation = ({
  projectId,
  onClose,
}: {
  projectId?: Project["id"]
  onClose?: () => void
}) => {
  const [open, setOpen] = useState(false)
  const [groups, setGroups] = useState<{ id: number; name: string }[]>([])
  const form = useForm<FormInputs>()
  const settings = useWatcher(tenantState).settings

  const { features, labels } = settings

  const handleClose = () => {
    setOpen(false)
  }

  useEffectOnce(() => {
    tasksApi.getAllOpGroups().then((groups) => {
      setGroups(formatGroups(groups))
    })
  })

  const onSubmit = async (values: FormInputs) => {
    try {
      const body: Partial<Operation> = { ...values, opGroupId: undefined }

      const opGroupId = values.opGroupId?.at(0)

      if (!opGroupId) {
        form.setError("opGroupId", { type: "required" })
        return
      }

      if (features.time) {
        body.timePredicted =
          Number(values.hours ?? 0) * 60 + Number(values.minutes ?? 0)
      }
      if (features.normatives) {
        body.normative = Number(values.normative ?? 0)
      }

      if (features.salary) {
        body.salaryPredicted = Number(values.salaryPredicted ?? 0)
      }

      if (features.units) {
        body.unitsPredicted = Number(values.unitsPredicted ?? 0)
      }

      body.opGroupId = opGroupId
      body.projectId = projectId
      body.workerCountExpected = 1

      await tasksApi.createOperation(body)
      setOpen(false)
      onClose?.()
      toast("Uzdevums veigsmīgi izveidots", { type: "success" })
    } catch {
      toast("Uzdevums nav izveidots, lūdzu mēģiniet vēlreiz", { type: "error" })
    }
  }

  // Adding new task entries is disabled.
  return null

  // eslint-disable-next-line no-unreachable
  return (
    <>
      <Button onClick={() => setOpen(true)}>Pievienot uzdevumu</Button>
      <Sidepanel open={open} onClose={handleClose}>
        <Title>Pievienot jaunu uzdevumu</Title>
        <Form {...form}>
          <StyledForm onSubmit={form.handleSubmit(onSubmit)}>
            <DropdownField
              control={form.control}
              name="opGroupId"
              placeholder={labels?.["Operācija"] ?? "Operācija"}
              label={labels?.["Operācija"] ?? "Operācija"}
              options={groups}
              className={
                form.formState.errors.opGroupId ? "border-[var(--error)]" : ""
              }
            />

            {features.units && (
              <InputField
                control={form.control}
                name="unitsPredicted"
                label={"Paredzētās Vienības"}
                inputProps={{ type: "number" }}
              />
            )}

            {features.time && (
              <div className="grid grid-cols-2 gap-2">
                <InputField
                  control={form.control}
                  name="hours"
                  label={"Stundas"}
                  inputProps={{ type: "number" }}
                />
                <InputField
                  control={form.control}
                  name="minutes"
                  label={"Minūtes"}
                  inputProps={{ type: "number" }}
                />
              </div>
            )}

            {features.normatives && (
              <InputField
                control={form.control}
                name="normative"
                label={"Normatīvi"}
                inputProps={{ type: "number" }}
              />
            )}

            {features.salary && (
              <InputField
                control={form.control}
                name="salaryPredicted"
                label={"Summa"}
                inputProps={{ type: "number" }}
              />
            )}
            <ButtonRow>
              <Button
                variant="secondary"
                type="button"
                onClick={() => setOpen(false)}
              >
                Atcelt
              </Button>
              <Button type="submit">Pievienot</Button>
            </ButtonRow>
          </StyledForm>
        </Form>
      </Sidepanel>
    </>
  )
}
